import type { MorphConfig } from '../types.ts';
import { linear, slerp } from './interpolator.ts';

export class MorphController {
  private ctx: CanvasRenderingContext2D;
  private decodeFn: (z: Float32Array) => Promise<Float32Array>;
  private from: Float32Array | null = null;
  private to: Float32Array | null = null;
  private animationId: number | null = null;
  private isRunning = false;
  private startTime = 0;
  private config: MorphConfig | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    decodeFn: (z: Float32Array) => Promise<Float32Array>
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    this.ctx = ctx;
    this.decodeFn = decodeFn;
  }

  setFrom(point: Float32Array): void {
    this.from = new Float32Array(point);
  }

  setTo(point: Float32Array): void {
    this.to = new Float32Array(point);
  }

  getFrom(): Float32Array | null {
    return this.from;
  }

  getTo(): Float32Array | null {
    return this.to;
  }

  play(config: MorphConfig): void {
    this.stop();
    this.config = config;
    this.from = new Float32Array(config.from);
    this.to = new Float32Array(config.to);
    this.isRunning = true;
    this.startTime = performance.now();
    this.tick();
  }

  stop(): void {
    this.isRunning = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  isPlaying(): boolean {
    return this.isRunning;
  }

  private async tick(): Promise<void> {
    if (!this.isRunning || !this.config || !this.from || !this.to) return;

    const now = performance.now();
    const elapsed = now - this.startTime;
    const stepDuration = this.config.frameDurationMs;
    const totalDuration = this.config.steps * stepDuration;

    let t = 0;
    if (totalDuration > 0) {
      t = elapsed / totalDuration;
    }

    if (t >= 1) {
      if (this.config.loop) {
        this.startTime = now;
        t = 0;
      } else {
        t = 1;
        this.isRunning = false;
      }
    }

    const z = this.config.interpolation === 'spherical'
      ? slerp(this.from, this.to, t)
      : linear(this.from, this.to, t);

    try {
      const image = await this.decodeFn(z);
      this.drawImage(image);
    } catch {
    }

    if (this.isRunning) {
      this.animationId = requestAnimationFrame(() => this.tick());
    }
  }

  private drawImage(image: Float32Array): void {
    const size = 28;
    const imgData = this.ctx.createImageData(size, size);
    for (let i = 0; i < size * size; i++) {
      const val = Math.max(0, Math.min(255, Math.round(image[i] * 255)));
      imgData.data[i * 4] = val;
      imgData.data[i * 4 + 1] = val;
      imgData.data[i * 4 + 2] = val;
      imgData.data[i * 4 + 3] = 255;
    }
    this.ctx.imageSmoothingEnabled = false;
    this.ctx.putImageData(imgData, 0, 0);
  }
}
